import * as React from 'react';

import { safeJsonParse } from '../../utilities/helpers/safeJsonParse';
import { getLogger } from '../logger';

const logger = getLogger('sse:processInstanceUpdates', true);

const MAX_BACKOFF_MS = 30_000;

export const UpdateEvents = {
  UPDATE: 'update',
  INCREMENT: 'incremental',
  WORKFLOW_DEFINITION: 'workflow_definition',
  CANCELLED: 'cancelled',
} as const;

export type UpdateEvents = (typeof UpdateEvents)[keyof typeof UpdateEvents];

export type SSEConnectionStatus = 'connected' | 'connecting' | 'error';

export type SSEConnectionChange = {
  status: SSEConnectionStatus;
  reconnectAttempt: number;
  maxRetries: number;
  maxRetriesReached: boolean;
};

export const useSSEEvent = <T>({
  url,
  onMessage,
  onError,
  onExhausted,
  onConnectionChange,
  enabled = true,
  retryMs = 1000,
  maxRetries = 10,
}: {
  url: string;
  enabled?: boolean;
  onMessage: (data: T, eventType: UpdateEvents) => void;
  onError?: (error: string) => void;
  /**
   * Fired once the retry ladder is exhausted (`retryCount >= maxRetries`),
   * distinct from transient config/init errors surfaced via `onError`. When
   * omitted, exhaustion falls back to `onError` for backward compatibility.
   */
  onExhausted?: () => void;
  onConnectionChange?: (change: SSEConnectionChange) => void;
  retryMs?: number;
  maxRetries?: number;
}): void => {
  const onMessageRef = React.useRef(onMessage);
  const onErrorRef = React.useRef(onError);
  const onExhaustedRef = React.useRef(onExhausted);
  const onConnectionChangeRef = React.useRef(onConnectionChange);

  onMessageRef.current = onMessage;
  onErrorRef.current = onError;
  onExhaustedRef.current = onExhausted;
  onConnectionChangeRef.current = onConnectionChange;

  React.useEffect(() => {
    if (!enabled) return;
    if (!url) {
      logger.error('url parameter is required');
      onErrorRef.current?.('Missing url parameter');
      return;
    }

    let es: EventSource | null = null;
    let closed = false;
    let retryCount = 0;
    let hasReceivedMessage = false;
    let reconnectTO: ReturnType<typeof setTimeout> | null = null;

    const getDelay = (): number => {
      const base = typeof retryMs === 'number' && retryMs >= 0 ? retryMs : 1000;
      const exponential = base * Math.pow(2, retryCount);
      return Math.min(exponential, MAX_BACKOFF_MS);
    };

    const cleanup = (): void => {
      if (es) {
        es.removeEventListener(UpdateEvents.UPDATE, handleUpdate);
        es.removeEventListener('message', handleUpdate);
        es.removeEventListener(UpdateEvents.INCREMENT, handleUpdate);
        es.removeEventListener(UpdateEvents.WORKFLOW_DEFINITION, handleUpdate);
        es.removeEventListener(UpdateEvents.CANCELLED, handleUpdate);
        es.removeEventListener('open', handleOpen);
        es.removeEventListener('error', handleError);
        es.close();
        es = null;
      }
      if (reconnectTO) {
        clearTimeout(reconnectTO);
        reconnectTO = null;
      }
    };

    const notifyConnectionChange = (
      status: SSEConnectionStatus,
      reconnectAttempt: number,
      maxRetriesReached: boolean,
    ): void => {
      onConnectionChangeRef.current?.({
        status,
        reconnectAttempt,
        maxRetries,
        maxRetriesReached,
      });
    };

    const failConnection = (message: string): void => {
      logger.error(message);
      notifyConnectionChange('error', retryCount, true);
      onErrorRef.current?.(message);
      cleanup();
    };

    // Terminal retry-ladder exhaustion. Prefer the dedicated `onExhausted`
    // signal (used by the fallback facade to switch transports); fall back to
    // `onError` when no exhaustion handler is provided.
    const exhaust = (message: string): void => {
      if (onExhaustedRef.current) {
        logger.error(message);
        cleanup();
        onExhaustedRef.current();
        return;
      }
      failConnection(message);
    };

    const scheduleReconnect = (): void => {
      if (closed) return;
      if (reconnectTO) return;

      if (retryCount >= maxRetries) {
        exhaust('Connection lost. Please refresh the page or try again later.');
        return;
      }

      notifyConnectionChange('connecting', retryCount + 1, false);

      const delay = getDelay();
      logger.warn(`SSE reconnect attempt #${retryCount + 1} in ${delay}ms`);
      reconnectTO = setTimeout(() => {
        reconnectTO = null;
        retryCount += 1;
        connect();
      }, delay);
    };

    const handleOpen = (): void => {
      // 🛑 Not reset here; `handleUpdate` clears it once a message arrives. A
      // server that accepts the connection and then errors re-opens cleanly
      // every time, so resetting on open makes the ladder loop for ever.
      notifyConnectionChange('connected', 0, false);
    };

    const handleUpdate = (event: MessageEvent): void => {
      retryCount = 0;
      hasReceivedMessage = true;
      notifyConnectionChange('connected', 0, false);
      const parsedData = handleParsedMessage(event.data) as T;
      if (parsedData) {
        onMessageRef.current(parsedData, event.type as UpdateEvents);
      }
    };

    const handleError = (event: Event): void => {
      logger.error('SSE connection error', readServerRefusal(event) ?? event);

      if (es?.readyState === EventSource.CLOSED && !hasReceivedMessage) {
        if (retryCount >= maxRetries) {
          exhaust('Failed to connect to live updates');
          return;
        }

        scheduleReconnect();
        return;
      }

      scheduleReconnect();
    };

    const connect = (): void => {
      cleanup();
      try {
        es = new EventSource(url);
      } catch (e) {
        logger.error('Failed to initialize SSE EventSource', e);
        failConnection('Failed to initialize SSE EventSource');
        return;
      }

      es.addEventListener('open', handleOpen);
      es.addEventListener(UpdateEvents.UPDATE, handleUpdate);
      es.addEventListener(UpdateEvents.INCREMENT, handleUpdate);
      es.addEventListener(UpdateEvents.WORKFLOW_DEFINITION, handleUpdate);
      es.addEventListener(UpdateEvents.CANCELLED, handleUpdate);
      es.addEventListener('error', handleError);
    };

    connect();

    return () => {
      closed = true;
      cleanup();
    };
  }, [url, retryMs, maxRetries, enabled]);
};

/**
 * The reason a server-sent `error` frame carries, or `null` for a transport
 * failure: only the payload separates the two, since a dropped connection
 * dispatches a bare `Event` and an `error` frame a `MessageEvent`.
 *
 * Diagnostics only. The frame is **not** treated as terminal: the proxy sends
 * one for transient upstream failures and keeps the stream open, so the retry
 * ladder stays in charge of when to give up.
 */
function readServerRefusal(event: Event): string | null {
  const raw = (event as MessageEvent).data;
  if (typeof raw !== 'string' || !raw) return null;

  const parsed = safeJsonParse(raw) as { error?: unknown } | null;

  return typeof parsed?.error === 'string' && parsed.error ? parsed.error : raw;
}

function handleParsedMessage(raw: string) {
  const parsed = safeJsonParse(raw);
  if (!parsed) {
    logger.error('Error parsing SSE message');
    return null;
  }

  return parsed;
}
